import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { FaUser, FaImage, FaExclamationCircle } from 'react-icons/fa';

export default function BillboardListingItem({ listing, rank }) {
    const navigate = useNavigate();
    const auth = getAuth();
    const [userData, setUserData] = useState({ photoURL: null, name: null });
    const [posterImageError, setPosterImageError] = useState(false);
    const [userImageError, setUserImageError] = useState(false);
    const [loading, setLoading] = useState(false);

    // Validate listing has an id
    useEffect(() => {
        if (!listing?.id) {
            console.error("BillboardListingItem requires a listing object with an 'id' property");
        }
    }, [listing]);

    // Fetch user data when component mounts
    useEffect(() => {
        const fetchUserData = async () => {
            if (!listing?.userRef) return;

            try {
                const userRef = doc(db, 'users', listing.userRef);
                const userSnap = await getDoc(userRef);
                
                if (userSnap.exists()) {
                    const userData = userSnap.data();
                    setUserData({
                        photoURL: userData.photoURL || null,
                        name: userData.name || null,
                    });
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };

        fetchUserData();
    }, [listing?.userRef]);

    const formatPrice = (price) => {
        if (price === 'Negotiable') return price;
        if (price == null) return 'N/A';
        
        try {
            // Convert to number and format with commas
            const numPrice = typeof price === 'string' ? parseFloat(price) : price;
            return numPrice.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        } catch (e) {
            return 'N/A';
        }
    };

    const handleListingClick = () => {
        const user = auth.currentUser;
        
        if (!user) {
            toast.error('Please sign in to view listing details', {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            navigate('/welcome');
            return;
        }
        
        navigate(`/listing/${listing.id}`);
    };

    // Don't render if listing doesn't have an id
    if (!listing?.id) {
        return null;
    }

    // Get the first image URL or use placeholder
    const imageUrl = listing.imageUrls?.[0] || null;
    
    // Format price display
    const priceDisplay = listing.price != null 
        ? `${listing.currency || ''} ${formatPrice(listing.price)}`.trim()
        : 'N/A';

    return (
        <div 
            onClick={handleListingClick}
            className="cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98]"
        >
            <div className="bg-gray-800 rounded-xl shadow-lg mb-4 p-4">
                {/* Responsive layout using Tailwind */}
                <div className="hidden md:block">
                    {/* Desktop Layout */}
                    <div className="flex items-center">
                        {/* Rank Number */}
                        <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-lg">
                                {rank}
                            </span>
                        </div>

                        <div className="ml-4 flex-grow flex items-center">
                            {/* Listing Cover Image */}
                            <div className="w-24 h-36 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                                {!posterImageError && imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={listing.title || 'Listing'}
                                        className="w-full h-full object-cover"
                                        onError={() => setPosterImageError(true)}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                                        <FaImage size={32} />
                                    </div>
                                )}
                            </div>

                            <div className="ml-4 flex-grow">
                                <div className="flex items-center">
                                    {/* User Profile Image */}
                                    <div className="w-12 h-12 bg-gray-700 rounded-full overflow-hidden flex-shrink-0">
                                        {!userImageError && userData.photoURL ? (
                                            <img
                                                src={userData.photoURL}
                                                alt={userData.name || 'User'}
                                                className="w-full h-full object-cover"
                                                onError={() => setUserImageError(true)}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-500">
                                                <FaUser size={20} />
                                            </div>
                                        )}
                                    </div>

                                    <div className="ml-4 flex-grow">
                                        {/* Title */}
                                        <h3 className="text-white font-semibold text-lg truncate">
                                            {listing.title || 'Untitled'}
                                        </h3>
                                        
                                        {/* User Name */}
                                        <p className="text-gray-400 text-sm truncate">
                                            {userData.name || 'Unknown Seller'}
                                        </p>
                                        
                                        {/* Price */}
                                        <p className="text-gray-400 text-sm mt-1">
                                            {priceDisplay}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="md:hidden">
                    {/* Mobile Layout */}
                    <div className="flex items-start mb-3">
                        {/* Rank Number */}
                        <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-lg">
                                {rank}
                            </span>
                        </div>

                        <div className="ml-4 flex-grow">
                            {/* Title */}
                            <h3 className="text-white font-semibold text-lg truncate">
                                {listing.title || 'Untitled'}
                            </h3>
                            
                            {/* Price */}
                            <p className="text-gray-400 text-sm mt-1">
                                {priceDisplay}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center">
                        {/* Listing Cover Image */}
                        <div className="w-20 h-28 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                            {!posterImageError && imageUrl ? (
                                <img
                                    src={imageUrl}
                                    alt={listing.title || 'Listing'}
                                    className="w-full h-full object-cover"
                                    onError={() => setPosterImageError(true)}
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-500">
                                    <FaImage size={24} />
                                </div>
                            )}
                        </div>

                        <div className="ml-4 flex-grow">
                            <div className="flex items-center">
                                {/* User Profile Image */}
                                <div className="w-10 h-10 bg-gray-700 rounded-full overflow-hidden flex-shrink-0">
                                    {!userImageError && userData.photoURL ? (
                                        <img
                                            src={userData.photoURL}
                                            alt={userData.name || 'User'}
                                            className="w-full h-full object-cover"
                                            onError={() => setUserImageError(true)}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-500">
                                            <FaUser size={16} />
                                        </div>
                                    )}
                                </div>

                                {/* User Name */}
                                <div className="ml-3 flex-grow">
                                    <p className="text-gray-400 text-sm truncate">
                                        {userData.name || 'Unknown Seller'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}